import { TableRow, TableCell, Chip, Box, Avatar, Typography } from '@mui/material'
import React from 'react'
import { fullName } from '../../../domain/Account/AccountDisplay'
import { CustomerCageWithAnimalsDetailsDto } from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { getEmojiAnimalSex, getEmojiSpecies, getReadableSpecies } from '../../../domain/Animal/AnimalDisplay'
import { colors } from '../../theme'
import { AssociationEditCageModal } from './AssociationEditCageModal'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { getAnimalProfilePhotoSrc } from '../../../utils/S3-links'
import { useNavigate } from 'react-router-dom'
import { PATHS } from '../../PATHS'
import { getSoftChipSx } from '../../common/chipStyles'

interface Props {
  cage: CustomerCageWithAnimalsDetailsDto
  setCagesList: React.Dispatch<React.SetStateAction<CustomerCageWithAnimalsDetailsDto[]>>
  cagesList: CustomerCageWithAnimalsDetailsDto[]
}

export const AssociationCageItem: React.FC<Props> = ({ cage, setCagesList, cagesList }) => {
  const accountsStore = useAccountsStore()
  const memberInCharge = accountsStore.members.find((member) => member.id === cage.memberInChargeId)
  const dialogOpenedState = useDialogOpened()
  const navigate = useNavigate()
  const animalsCount = cage.animals?.length || 0
  const capacity = cage.capacity || 0

  const occupancyColor =
    animalsCount > capacity
      ? '#D76A7C'
      : animalsCount === capacity
      ? '#F1C96D'
      : animalsCount === 0
      ? '#CBD5E2'
      : '#89A97F'

  const occupancyLabel =
    animalsCount > capacity
      ? `Surcharge ${animalsCount}/${capacity}`
      : animalsCount === capacity
      ? `Occupée ${animalsCount}/${capacity}`
      : animalsCount === 0
      ? `Vacante ${animalsCount}/${capacity}`
      : `Partielle ${animalsCount}/${capacity}`

  const handleClick = () => {
    dialogOpenedState.openDialog()
  }
  return (
    <>
      <TableRow
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
        }}
      >
        <TableCell component="th" scope="row">
          <Typography sx={{ color: '#203864', fontSize: 13.5, fontWeight: 700, textTransform: 'lowercase' }}>
            box {cage.cageNumber}
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row">
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            {cage.suitableForSpecies.map((species) => (
              <Chip
                key={species}
                label={getReadableSpecies(species)}
                sx={getSoftChipSx(colors.blueSky, { height: 24, fontSize: 11.25, labelWeight: 500 })}
              />
            ))}
          </Box>
        </TableCell>

        <TableCell component="th" scope="row">
          {`${cage.capacity} ${cage.capacity > 1 ? 'animaux' : 'animal'}`}
        </TableCell>

        <TableCell component="th" scope="row">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.85 }}>
            {cage.animals?.length && cage.animals.length > 0
              ? cage.animals?.map((animal) => (
                  <Box
                    key={animal.id}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`${PATHS.animals.absolute}/${animal.id}`)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(`${PATHS.animals.absolute}/${animal.id}`)
                      }
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      pl: 0.45,
                      pr: 1.1,
                      py: 0.45,
                      borderRadius: '999px',
                      border: '1px solid rgba(122, 129, 151, 0.18)',
                      backgroundColor: '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(105, 141, 172, 0.05)',
                        borderColor: 'rgba(105, 141, 172, 0.24)',
                      },
                    }}
                  >
                    <Avatar
                      alt={`Photo de ${animal.name}`}
                      src={animal.profileImageKey && getAnimalProfilePhotoSrc(animal.profileImageKey)}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: '#203864',
                          fontSize: 12.25,
                          fontWeight: 600,
                          lineHeight: 1.15,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {animal.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#7A8197',
                          fontSize: 10.5,
                          fontWeight: 500,
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getEmojiSpecies(animal.species)} {animal.sex ? getEmojiAnimalSex(animal.sex) : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))
              : '..'}
          </Box>
        </TableCell>

        <TableCell component="th" scope="row">
          <Chip
            label={occupancyLabel}
            sx={getSoftChipSx(occupancyColor, {
              height: 26,
              fontSize: 11.5,
              labelWeight: 600,
              emphasis: true,
            })}
          />
        </TableCell>

        <TableCell component="th" scope="row">
          {memberInCharge ? fullName(memberInCharge) : '..'}
        </TableCell>
      </TableRow>
      <AssociationEditCageModal
        cage={cage}
        setCagesList={setCagesList}
        cagesList={cagesList}
        dialogOpenedState={dialogOpenedState}
      />
    </>
  )
}
